import type {ConfirmedCompatiblePart} from "@ri2/db/client"

/** Normalize OEM / MPN for comparison (trim, collapse spaces, upper-case). */
export const normalizePartMpnKey = (mpn: string): string =>
  mpn.trim().replace(/\s+/g, "").toUpperCase()

const PART_SHORTCODE_RE = /\[\[part:([^\]]+)\]\]/g

/**
 * Unique OEM keys in the order they first appear in `[[part:OEM]]` shortcodes.
 */
export const getOrderedShortcodeMpnKeys = (message: string): string[] => {
  const ordered: string[] = []
  const seen = new Set<string>()
  for (const m of message.matchAll(PART_SHORTCODE_RE)) {
    const raw = m[1]?.trim() ?? ""
    const key = normalizePartMpnKey(raw)
    if (!key || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  return ordered
}

/**
 * Puts parts that appear in `[[part:…]]` shortcodes first, in message order;
 * remaining parts keep their original relative order (extras at the end).
 */
export const sortConfirmedPartsByMessageShortcodes = (
  parts: ConfirmedCompatiblePart[],
  message: string,
): ConfirmedCompatiblePart[] => {
  const orderedKeys = getOrderedShortcodeMpnKeys(message)
  const rank = new Map(orderedKeys.map((k, i) => [k, i]))
  const seen = new Set<string>()
  const uniqueParts = parts.filter((part) => {
    const normalizedMpn = normalizePartMpnKey(part.manufacturerPartNumber)
    const key = normalizedMpn || `id:${part.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return uniqueParts
    .map((p, originalIndex) => ({p, originalIndex}))
    .sort((a, b) => {
      const ra = rank.get(normalizePartMpnKey(a.p.manufacturerPartNumber))
      const rb = rank.get(normalizePartMpnKey(b.p.manufacturerPartNumber))
      const aMentioned = ra !== undefined
      const bMentioned = rb !== undefined
      if (aMentioned && bMentioned && ra !== rb) return ra - rb
      if (aMentioned !== bMentioned) return aMentioned ? -1 : 1
      return a.originalIndex - b.originalIndex
    })
    .map(({p}) => p)
}
